import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { BACKEND_URL, DEFAULT_QR_CODE_URL } from '../config';
import PrintTestModal from './Shared/PrintTestModal';
import BluetoothPrinterScanner from './BluetoothPrinterScanner';

function PrinterManagement({ theme }) {
    const { t } = useLanguage();

    // Combined settings state
    const [settings, setSettings] = useState({
        printerType: 'bluetooth',
        paperWidth: 32,
        baudRate: 9600,
        fontSize: 'normal',
        businessName: 'CERVECERIA GIBBONS',
        address: 'C. ESCORZA 848',
        taxId: 'CGI181112777',
        footerMessage1: 'Ahorremos el agua,',
        footerMessage2: 'Tomemos cerveza',
        footerMessage3: '¡Gracias!',
        showQrCode: false,
        qrCodeUrl: DEFAULT_QR_CODE_URL,
        qrCodeText: 'Escanea para visitar:',
        includeIva: true,
        paperCut: 'partial'
    });

    const [saveStatus, setSaveStatus] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [loading, setLoading] = useState(true);
    const [showTestModal, setShowTestModal] = useState(false);
    const [isBluetoothSupported, setIsBluetoothSupported] = useState(false);
    const [isSerialSupported, setIsSerialSupported] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Load configuration from API
        loadConfiguration();

        // Check browser compatibility
        setIsBluetoothSupported('bluetooth' in navigator);
        setIsSerialSupported('serial' in navigator);

        // Detect iOS device
        setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent));
    }, []);

    const loadConfiguration = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BACKEND_URL}/api/printer-settings`);
            if (response.ok) {
                const data = await response.json();
                setSettings(prevSettings => ({ ...prevSettings, ...data }));
            } else {
                console.error('Failed to load printer settings');
            }
        } catch (error) {
            console.error('Error loading configuration:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveConfiguration = async () => {
        try {
            setSaveStatus(t('saving') || 'Saving...');

            const response = await fetch(`${BACKEND_URL}/api/printer-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings)
            });

            if (response.ok) {
                // Also save to localStorage for offline access and backward compatibility
                localStorage.setItem('printerConfig', JSON.stringify({
                    width: settings.paperWidth,
                    baudRate: settings.baudRate,
                    fontSize: settings.fontSize,
                    printerType: settings.printerType
                }));
                localStorage.setItem('receiptFormat', JSON.stringify({
                    businessName: settings.businessName,
                    address: settings.address,
                    taxId: settings.taxId,
                    footerMessage1: settings.footerMessage1,
                    footerMessage2: settings.footerMessage2,
                    footerMessage3: settings.footerMessage3,
                    showQrCode: settings.showQrCode,
                    qrCodeUrl: settings.qrCodeUrl,
                    qrCodeText: settings.qrCodeText,
                    includeIva: settings.includeIva,
                    paperCut: settings.paperCut
                }));

                setSaveStatus(t('settingsSaved'));
            } else {
                const error = await response.json();
                setSaveStatus(t('error') + ': ' + (error.error || 'Unknown error'));
            }
            setTimeout(() => setSaveStatus(''), 3000);
        } catch (error) {
            console.error('Error saving configuration:', error);
            setSaveStatus(t('error') + ': ' + error.message);
            setTimeout(() => setSaveStatus(''), 3000);
        }
    };

    const handleResetToDefaults = async () => {
        if (window.confirm(t('resetToDefaultsConfirmation') || 'Reset all printer settings to defaults?')) {
            const defaultSettings = {
                printerType: 'bluetooth',
                paperWidth: 32,
                baudRate: 9600,
                fontSize: 'normal',
                businessName: 'CERVECERIA GIBBONS',
                address: 'C. ESCORZA 848',
                taxId: 'CGI181112777',
                footerMessage1: 'Ahorremos el agua,',
                footerMessage2: 'Tomemos cerveza',
                footerMessage3: '¡Gracias!',
                showQrCode: false,
                qrCodeUrl: DEFAULT_QR_CODE_URL,
                qrCodeText: 'Escanea para visitar:',
                includeIva: true,
                paperCut: 'partial'
            };

            setSettings(defaultSettings);

            // Save to API
            try {
                const response = await fetch(`${BACKEND_URL}/api/printer-settings`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(defaultSettings)
                });

                if (response.ok) {
                    setSaveStatus(t('settingsReset') || 'Settings reset to defaults');
                } else {
                    setSaveStatus(t('error') + ': Failed to reset settings');
                }
            } catch (error) {
                console.error('Error resetting settings:', error);
                setSaveStatus(t('error') + ': ' + error.message);
            }

            setTimeout(() => setSaveStatus(''), 3000);
        }
    };

    const sectionStyle = {
        marginBottom: '1.5rem',
        padding: '1.5rem',
        backgroundColor: theme?.colors?.background || '#f5f5f5',
        borderRadius: theme?.borderRadius || 8,
        border: `1px solid ${theme?.colors?.border || '#ddd'}`
    };

    const inputStyle = {
        width: '100%',
        padding: '0.5rem',
        borderRadius: theme?.borderRadius || 4,
        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
        backgroundColor: theme?.colors?.surface || '#fff',
        color: theme?.colors?.text || '#000',
        fontSize: '1rem'
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '0.5rem',
        fontWeight: '600',
        color: theme?.colors?.text || '#000'
    };

    const buttonStyle = {
        padding: '0.75rem 1.5rem',
        backgroundColor: theme?.colors?.primary || '#1976d2',
        color: theme?.colors?.surface || '#fff',
        border: 'none',
        borderRadius: theme?.borderRadius || 6,
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '1rem',
        marginRight: '0.5rem'
    };

    return (
        <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: theme?.colors?.surface || '#fff',
            borderRadius: theme?.borderRadius || 8,
            boxShadow: theme?.boxShadow || '0 2px 8px rgba(0,0,0,0.1)'
        }}>
            <h2 style={{
                color: theme?.colors?.primary || '#1976d2',
                marginTop: 0,
                borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
                paddingBottom: '0.5rem'
            }}>
                🖨️ {t('printerManagement')}
            </h2>

            {/* Browser Compatibility Status */}
            <div style={{
                ...sectionStyle,
                backgroundColor: theme?.colors?.surface || '#fff',
                border: `2px solid ${(!isBluetoothSupported && !isSerialSupported)
                    ? (theme?.colors?.error || '#d32f2f')
                    : (theme?.colors?.success || '#2e7d32')}`
            }}>
                <h3 style={{ marginTop: 0, color: theme?.colors?.text || '#000' }}>{t('browserCompatibility')}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div style={{ color: theme?.colors?.text || '#000' }}>
                        <strong>{t('webBluetoothApi')}:</strong>
                        <span style={{
                            marginLeft: '0.5rem',
                            color: isBluetoothSupported ? (theme?.colors?.success || '#2e7d32') : (theme?.colors?.error || '#d32f2f')
                        }}>
                            {isBluetoothSupported ? '✅ ' + t('available') : '❌ ' + t('notAvailable')}
                        </span>
                    </div>
                    <div style={{ color: theme?.colors?.text || '#000' }}>
                        <strong>{t('webSerialApi')}:</strong>
                        <span style={{
                            marginLeft: '0.5rem',
                            color: isSerialSupported ? (theme?.colors?.success || '#2e7d32') : (theme?.colors?.error || '#d32f2f')
                        }}>
                            {isSerialSupported ? '✅ ' + t('available') : '❌ ' + t('notAvailable')}
                        </span>
                    </div>
                </div>
                {(!isBluetoothSupported && !isSerialSupported) && (
                    <div style={{
                        marginTop: '1rem',
                        marginBottom: 0,
                        color: theme?.colors?.error || '#d32f2f',
                        fontWeight: 'bold'
                    }}>
                        {isIOS ? (
                            <>
                                <p style={{ margin: '0 0 0.75rem 0' }}>
                                    ⚠️ Safari/Chrome on iOS do not support Bluetooth printing.
                                </p>
                                <p style={{ margin: '0 0 0.75rem 0', fontWeight: 'normal' }}>
                                    To use Bluetooth printing on iPhone/iPad, please install <strong>Bluefy Browser</strong> from the App Store:
                                </p>
                                <a
                                    href="https://apps.apple.com/app/bluefy-web-ble-browser/id1492822055"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-block',
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: theme?.colors?.primary || '#1976d2',
                                        color: '#fff',
                                        textDecoration: 'none',
                                        borderRadius: theme?.borderRadius || '8px',
                                        fontWeight: 'bold',
                                        marginTop: '0.5rem'
                                    }}
                                >
                                    📲 Install Bluefy Browser
                                </a>
                            </>
                        ) : (
                            <p style={{ margin: 0 }}>
                                ⚠️ {t('useChromeEdgeForThermalPrinters')}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Bluetooth Printer Discovery & Naming */}
            <BluetoothPrinterScanner theme={theme} />

            {/* Printer Configuration */}
            <div style={sectionStyle}>
                <h3 style={{ marginTop: 0, color: theme?.colors?.text }}>⚙️ {t('printerConfiguration')}</h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                        <label style={labelStyle}>{t('printerType')}</label>
                        <select
                            value={settings.printerType}
                            onChange={(e) => setSettings({ ...settings, printerType: e.target.value })}
                            style={inputStyle}
                        >
                            <option value="bluetooth">{t('bluetoothPrinter')} {isBluetoothSupported ? '✅' : '❌'}</option>
                            <option value="serial">{t('usbSerialPrinter')} {isSerialSupported ? '✅' : '❌'}</option>
                        </select>
                    </div>

                    <div>
                        <label style={labelStyle}>{t('paperWidth')}</label>
                        <select
                            value={settings.paperWidth}
                            onChange={(e) => setSettings({ ...settings, width: Number(e.target.value) })}
                            style={inputStyle}
                        >
                            <option value={24}>24 {t('chars')} - 58mm {t('narrow')}</option>
                            <option value={28}>28 {t('chars')} - 58mm PT210</option>
                            <option value={32}>32 {t('chars')} - 58mm {t('wide')}</option>
                            <option value={42}>42 {t('chars')} - 80mm</option>
                        </select>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                        <label style={labelStyle}>{t('baudRate')} ({t('serialOnly')})</label>
                        <select
                            value={settings.baudRate}
                            onChange={(e) => setSettings({ ...settings, baudRate: Number(e.target.value) })}
                            style={inputStyle}
                            disabled={settings.printerType === 'bluetooth'}
                        >
                            <option value={9600}>9600 {t('baud')}</option>
                            <option value={19200}>19200 {t('baud')}</option>
                            <option value={38400}>38400 {t('baud')}</option>
                            <option value={115200}>115200 {t('baud')}</option>
                        </select>
                    </div>

                    <div>
                        <label style={labelStyle}>{t('fontSize')}</label>
                        <select
                            value={settings.fontSize}
                            onChange={(e) => setSettings({ ...settings, fontSize: e.target.value })}
                            style={inputStyle}
                        >
                            <option value="small">{t('small')}</option>
                            <option value="normal">{t('normal')}</option>
                            <option value="large">{t('large')}</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Receipt Format Configuration */}
            <div style={sectionStyle}>
                <h3 style={{ marginTop: 0, color: theme?.colors?.text }}>📄 {t('receiptFormat')}</h3>

                {/* Header Information */}
                <div style={{ marginBottom: '1rem' }}>
                    <h4 style={{ color: theme?.colors?.textSecondary }}>{t('headerInformation')}</h4>
                    <div style={{ marginBottom: '0.75rem' }}>
                        <label style={labelStyle}>{t('businessName')}</label>
                        <input
                            type="text"
                            value={settings.businessName}
                            onChange={(e) => setSettings({ ...settings, businessName: e.target.value })}
                            style={inputStyle}
                        />
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                        <label style={labelStyle}>{t('address')}</label>
                        <input
                            type="text"
                            value={settings.address}
                            onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                            style={inputStyle}
                        />
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                        <label style={labelStyle}>{t('taxId')} (RFC)</label>
                        <input
                            type="text"
                            value={settings.taxId}
                            onChange={(e) => setSettings({ ...settings, taxId: e.target.value })}
                            style={inputStyle}
                        />
                    </div>
                </div>

                {/* Footer Messages */}
                <div style={{ marginBottom: '1rem' }}>
                    <h4 style={{ color: theme?.colors?.textSecondary }}>{t('footerMessages')}</h4>
                    <div style={{ marginBottom: '0.75rem' }}>
                        <label style={labelStyle}>{t('line')} 1</label>
                        <input
                            type="text"
                            value={settings.footerMessage1}
                            onChange={(e) => setSettings({ ...settings, footerMessage1: e.target.value })}
                            style={inputStyle}
                        />
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                        <label style={labelStyle}>{t('line')} 2</label>
                        <input
                            type="text"
                            value={settings.footerMessage2}
                            onChange={(e) => setSettings({ ...settings, footerMessage2: e.target.value })}
                            style={inputStyle}
                        />
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                        <label style={labelStyle}>{t('line')} 3</label>
                        <input
                            type="text"
                            value={settings.footerMessage3}
                            onChange={(e) => setSettings({ ...settings, footerMessage3: e.target.value })}
                            style={inputStyle}
                        />
                    </div>
                </div>

                {/* Options */}
                <div>
                    <h4 style={{ color: theme?.colors?.textSecondary }}>{t('options')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={settings.includeIva}
                                onChange={(e) => setSettings({ ...settings, includeIva: e.target.checked })}
                                style={{ marginRight: '0.5rem' }}
                            />
                            <span>{t('includeIvaBreakdown')}</span>
                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={settings.showQrCode}
                                onChange={(e) => setSettings({ ...settings, showQrCode: e.target.checked })}
                                style={{ marginRight: '0.5rem' }}
                            />
                            <span>{t('includeQrCode')}</span>
                        </label>

                        {settings.showQrCode && (
                            <div style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                                <label style={labelStyle}>{t('qrCodeUrl')}</label>
                                <input
                                    type="text"
                                    value={settings.qrCodeUrl}
                                    onChange={(e) => setSettings({ ...settings, qrCodeUrl: e.target.value })}
                                    style={inputStyle}
                                    placeholder="https://example.com"
                                />
                                <label style={{ ...labelStyle, marginTop: '0.75rem' }}>{t('qrCodeText')}</label>
                                <input
                                    type="text"
                                    value={settings.qrCodeText}
                                    onChange={(e) => setSettings({ ...settings, qrCodeText: e.target.value })}
                                    style={inputStyle}
                                    placeholder="Escanea para visitar:"
                                />
                            </div>
                        )}

                        <div style={{ marginTop: '0.5rem' }}>
                            <label style={labelStyle}>{t('paperCut')}</label>
                            <select
                                value={settings.paperCut}
                                onChange={(e) => setSettings({ ...settings, paperCut: e.target.value })}
                                style={inputStyle}
                            >
                                <option value="none">{t('none')}</option>
                                <option value="partial">{t('partialCut')}</option>
                                <option value="full">{t('fullCut')}</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Status Message */}
            {saveStatus && (
                <div style={{
                    padding: '1rem',
                    marginBottom: '1rem',
                    backgroundColor: saveStatus.includes(t('error'))
                        ? (theme?.colors?.errorLight || '#ffebee')
                        : (theme?.colors?.successLight || '#e8f5e9'),
                    color: saveStatus.includes(t('error'))
                        ? (theme?.colors?.error || '#d32f2f')
                        : (theme?.colors?.success || '#2e7d32'),
                    borderRadius: theme?.borderRadius || 8,
                    textAlign: 'center',
                    fontWeight: 'bold'
                }}>
                    {saveStatus}
                </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                    onClick={handleSaveConfiguration}
                    style={buttonStyle}
                >
                    💾 {t('saveSettings')}
                </button>

                <button
                    onClick={() => setShowTestModal(true)}
                    style={{
                        ...buttonStyle,
                        backgroundColor: theme?.colors?.success || '#4caf50'
                    }}
                >
                    🖨️ {t('printTest')}
                </button>

                <button
                    onClick={handleResetToDefaults}
                    style={{
                        ...buttonStyle,
                        backgroundColor: theme?.colors?.textSecondary || '#666'
                    }}
                >
                    🔄 {t('resetToDefaults')}
                </button>
            </div>

            {/* Test Print Modal */}
            {showTestModal && (
                <PrintTestModal
                    onClose={() => setShowTestModal(false)}
                    settings={settings}
                />
            )}
        </div>
    );
}

export default PrinterManagement;
